import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { AuthState, User, LoginFormData, RegisterFormData } from '../types';

// Valeurs par défaut du contexte
const defaultAuthState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Interface pour les fonctions du contexte
interface AuthContextType extends AuthState {
  login: (credentials: LoginFormData) => Promise<void>;
  register: (userData: RegisterFormData) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

// Création du contexte
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Props du provider
interface AuthProviderProps {
  children: ReactNode;
}

// Provider du contexte
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(defaultAuthState);
  const navigate = useNavigate();

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const verifyAuth = async () => {
      const token = authService.getToken();
      
      if (!token) {
        setAuthState({
          ...defaultAuthState,
          isLoading: false,
        });
        return;
      }
      
      try {
        const user = await authService.verifyToken();
        setAuthState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error('Error verifying token:', error);
        authService.logout();
        setAuthState({
          ...defaultAuthState,
          isLoading: false,
        });
      }
    };
    
    verifyAuth();
  }, []); // Ajout d'un tableau de dépendances vide pour s'assurer qu'il ne s'exécute qu'une seule fois au chargement

  // Fonction de connexion
  const login = async (credentials: LoginFormData) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const { user, token } = await authService.login(credentials);
      setAuthState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      navigate('/');
    } catch (error: any) {
      console.error('Login error:', error);
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.error || 'Erreur de connexion',
      }));
    }
  };

  // Fonction d'inscription
  const register = async (userData: RegisterFormData) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const { user, token } = await authService.register(userData);
      setAuthState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      navigate('/');
    } catch (error: any) {
      console.error('Register error:', error);
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.error || 'Erreur d\'inscription',
      }));
    }
  };

  // Fonction de déconnexion
  const logout = () => {
    authService.logout();
    setAuthState({
      ...defaultAuthState,
      isLoading: false,
    });
    navigate('/login');
  };

  // Fonction pour effacer les erreurs
  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null }));
  };

  // Valeur du contexte
  const contextValue: AuthContextType = {
    ...authState,
    login,
    register,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
